type FeeSplitSlidersProps = {
	feeSplitCreator: number;
	feeSplitTreasury: number;
	feeSplitProtocol: number;
	onCreatorChange: (value: number) => void;
	onTreasuryChange: (value: number) => void;
	onProtocolChange: (value: number) => void;
};

export function FeeSplitSliders({
	feeSplitCreator,
	feeSplitTreasury,
	feeSplitProtocol,
	onCreatorChange,
	onTreasuryChange,
	onProtocolChange,
}: FeeSplitSlidersProps) {
	const total = feeSplitCreator + feeSplitTreasury + feeSplitProtocol;
	const isValid = total === 100;

	const handleCreatorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onCreatorChange(Number.parseInt(e.target.value, 10));
	};

	const handleTreasuryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onTreasuryChange(Number.parseInt(e.target.value, 10));
	};

	const handleProtocolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onProtocolChange(Number.parseInt(e.target.value, 10));
	};

	return (
		<div className="space-y-6">
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
				{/* Creator Fee */}
				<div className="space-y-3">
					<div className="flex justify-between items-center">
						<label htmlFor="creator-fee" className="text-sm font-medium text-pastel-black">
							Creator Fee
						</label>
						<span className="text-sm font-semibold text-pastel-vibrant-blue">
							{feeSplitCreator}%
						</span>
					</div>
					<input
						id="creator-fee"
						type="range"
						min="0"
						max="100"
						value={feeSplitCreator}
						onChange={handleCreatorChange}
						className="
							w-full h-2 bg-pastel-light-gray rounded-lg appearance-none cursor-pointer
							slider-thumb:appearance-none slider-thumb:w-4 slider-thumb:h-4
							slider-thumb:bg-pastel-vibrant-blue slider-thumb:rounded-full
							slider-thumb:cursor-pointer slider-thumb:shadow-md
							focus:outline-none focus:ring-2 focus:ring-pastel-vibrant-blue/30
						"
						style={{
							background: `linear-gradient(to right, var(--color-pastel-vibrant-blue) 0%, var(--color-pastel-vibrant-blue) ${feeSplitCreator}%, var(--color-pastel-light-gray) ${feeSplitCreator}%, var(--color-pastel-light-gray) 100%)`,
						}}
					/>
					<p className="text-xs text-pastel-gray">Revenue for token creator</p>
				</div>

				{/* Treasury Fee */}
				<div className="space-y-3">
					<div className="flex justify-between items-center">
						<label htmlFor="treasury-fee" className="text-sm font-medium text-pastel-black">
							Treasury Fee
						</label>
						<span className="text-sm font-semibold text-pastel-vibrant-pink">
							{feeSplitTreasury}%
						</span>
					</div>
					<input
						id="treasury-fee"
						type="range"
						min="0"
						max="100"
						value={feeSplitTreasury}
						onChange={handleTreasuryChange}
						className="
							w-full h-2 bg-pastel-light-gray rounded-lg appearance-none cursor-pointer
							slider-thumb:appearance-none slider-thumb:w-4 slider-thumb:h-4
							slider-thumb:bg-pastel-vibrant-pink slider-thumb:rounded-full
							slider-thumb:cursor-pointer slider-thumb:shadow-md
							focus:outline-none focus:ring-2 focus:ring-pastel-vibrant-pink/30
						"
						style={{
							background: `linear-gradient(to right, var(--color-pastel-vibrant-pink) 0%, var(--color-pastel-vibrant-pink) ${feeSplitTreasury}%, var(--color-pastel-light-gray) ${feeSplitTreasury}%, var(--color-pastel-light-gray) 100%)`,
						}}
					/>
					<p className="text-xs text-pastel-gray">Community treasury funding</p>
				</div>

				{/* Protocol Fee */}
				<div className="space-y-3">
					<div className="flex justify-between items-center">
						<label htmlFor="protocol-fee" className="text-sm font-medium text-pastel-black">
							Protocol Fee
						</label>
						<span className="text-sm font-semibold text-pastel-vibrant-green">
							{feeSplitProtocol}%
						</span>
					</div>
					<input
						id="protocol-fee"
						type="range"
						min="0"
						max="100"
						value={feeSplitProtocol}
						onChange={handleProtocolChange}
						className="
							w-full h-2 bg-pastel-light-gray rounded-lg appearance-none cursor-pointer
							slider-thumb:appearance-none slider-thumb:w-4 slider-thumb:h-4
							slider-thumb:bg-pastel-vibrant-green slider-thumb:rounded-full
							slider-thumb:cursor-pointer slider-thumb:shadow-md
							focus:outline-none focus:ring-2 focus:ring-pastel-vibrant-green/30
						"
						style={{
							background: `linear-gradient(to right, var(--color-pastel-vibrant-green) 0%, var(--color-pastel-vibrant-green) ${feeSplitProtocol}%, var(--color-pastel-light-gray) ${feeSplitProtocol}%, var(--color-pastel-light-gray) 100%)`,
						}}
					/>
					<p className="text-xs text-pastel-gray">Platform maintenance & development</p>
				</div>
			</div>

			{/* Total Validation Indicator */}
			<div className="flex items-center justify-center space-x-3 p-4 bg-pastel-light-gray/50 rounded-lg border">
				<div
					className={`
						w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold
						${isValid ? "bg-green-500 text-white" : "bg-red-500 text-white"}
					`}
				>
					{isValid ? "✓" : "!"}
				</div>
				<div>
					<p className={`font-medium ${isValid ? "text-green-700" : "text-red-700"}`}>
						Total: {total}%
					</p>
					<p className="text-sm text-pastel-gray">
						{isValid ? "Perfect! Ready to proceed." : "Fee splits must sum to exactly 100%"}
					</p>
				</div>
			</div>
		</div>
	);
}
